import { useEffect, useState } from 'react';
import {
  Modal, ModalPortal, ModalOverlay, ModalContent, ModalHeader, ModalTitle, ModalDescription, Input,
} from '@klaro7/ui';

import { resolveUploadUrl } from '@/lib/uploads';
import { useAdminUsers } from '@/features/users/hooks/useAdminUsers';
import { useOpenThread } from '../hooks/useSupportUsers';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the opened/existing conversation id after a client is picked. */
  onCreated: (conversationId: string) => void;
}

/**
 * Staff picks a CLIENT (role user) to open — or resume — their support thread.
 * Search is server-side (GET /users?q=&role=user): the client base is too big
 * to load and filter in memory like the pro-side modal does.
 */
export function NewSupportUserThreadModal({ open, onOpenChange, onCreated }: Props) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading, isFetching } = useAdminUsers({
    role: 'user',
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    ...(debounced ? { q: debounced } : {}),
  });
  const openThread = useOpenThread();

  const users = data?.data ?? [];

  const pick = (userId: string) => {
    if (openThread.isPending) return;
    openThread.mutate(userId, {
      onSuccess: (conv) => {
        onCreated(conv.id);
        onOpenChange(false);
        setSearch('');
      },
    });
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalPortal>
        <ModalOverlay />
        <ModalContent className="max-w-md">
          <ModalHeader>
            <ModalTitle>Nuova conversazione</ModalTitle>
            <ModalDescription>
              Cerca un cliente per scrivergli nella chat di assistenza. Se ha già una conversazione, si riapre quella.
            </ModalDescription>
          </ModalHeader>

          <div className="mt-3">
            <Input
              placeholder="Cerca per nome, cognome o email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          <div className="mt-3 max-h-80 overflow-y-auto rounded-xl border border-neutral-200">
            {isLoading ? (
              <p className="p-4 text-sm text-neutral-400">Caricamento…</p>
            ) : users.length === 0 ? (
              <p className="p-4 text-sm text-neutral-400">
                {isFetching ? 'Ricerca…' : 'Nessun cliente trovato'}
              </p>
            ) : (
              users.map((u) => {
                const name = `${u.profile?.firstName ?? ''} ${u.profile?.lastName ?? ''}`.trim() || 'Cliente';
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => pick(u.id)}
                    disabled={openThread.isPending}
                    className="flex w-full items-center gap-3 border-b border-neutral-100 px-4 py-3 text-left last:border-b-0 hover:bg-neutral-50 disabled:opacity-50"
                  >
                    <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full bg-neutral-100">
                      {u.profile?.avatar && (
                        <img src={resolveUploadUrl(u.profile.avatar)} alt="" className="h-9 w-9 object-cover" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-semibold text-neutral-900">{name}</p>
                      <p className="truncate text-[11px] text-neutral-400">{u.email}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {openThread.isError && (
            <p className="mt-2 text-[11px] text-red-600">Impossibile aprire la conversazione. Riprova.</p>
          )}
        </ModalContent>
      </ModalPortal>
    </Modal>
  );
}
